import { cookieStorage } from '@muse-web/storages'

import { EXPERIMENT_MEMBER_COOKIE_NAME } from '@core/http-client'

import type {
    TExperimentAssignment,
    TExperimentCookieJar,
    TExperimentSlug,
    TExperimentsStorage,
    TStoredAssignment,
} from '../types/experimentTypes'

const LEDGER_MAX_AGE_SECONDS = 60 * 60 * 24 * 31
const ASSIGNMENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 7
const ASSIGNMENT_COOKIE_PREFIX = 'experiment-'

const writeCookie = (name: string, value: string, maxAge: number): boolean => {
    try {
        document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; Secure; SameSite=none;`

        return true
    } catch {
        return false
    }
}

export const experimentSlugsCookieStorage: TExperimentsStorage = {
    get(key) {
        const raw = cookieStorage.get(key)

        if (raw === undefined) {
            return null
        }

        try {
            const parsed: unknown = JSON.parse(raw)

            if (!Array.isArray(parsed)) {
                return null
            }

            return parsed.filter((slug): slug is TExperimentSlug => typeof slug === 'string')
        } catch {
            return null
        }
    },

    set(key, value) {
        writeCookie(key, JSON.stringify(value), LEDGER_MAX_AGE_SECONDS)
    },
}

const readMember = (value: unknown): string | undefined =>
    typeof value === 'string' && value !== '' ? value : undefined

const field = (source: object, key: string): unknown =>
    key in source ? Reflect.get(source, key) : undefined

const parseAssignment = (raw: string): TStoredAssignment | undefined => {
    try {
        const parsed: unknown = JSON.parse(raw)

        if (typeof parsed !== 'object' || parsed === null) {
            return undefined
        }

        const isSlim = typeof field(parsed, 'v') === 'number'
        const variation = field(parsed, isSlim ? 'v' : 'variation')
        const member = field(parsed, isSlim ? 'm' : 'member')
        const id = field(parsed, 'id')

        if (typeof variation !== 'number' || typeof id !== 'string' || !/^\d+$/.test(id)) {
            return undefined
        }

        return { id: Number(id), variation, member: readMember(member) }
    } catch {
        return undefined
    }
}

export const experimentCookieJar: TExperimentCookieJar = {
    getSubjectId() {
        // Empty is unbound, as in audio-web — without this the cookie is rewritten on every call.
        return cookieStorage.get(EXPERIMENT_MEMBER_COOKIE_NAME) || undefined
    },

    readAssignment(slug: TExperimentSlug) {
        const raw = cookieStorage.get(`${ASSIGNMENT_COOKIE_PREFIX}${slug}`)

        return raw === undefined || raw === '' ? undefined : parseAssignment(raw)
    },

    writeAssignment(slug: TExperimentSlug, assignment: TExperimentAssignment, subject?: string) {
        writeCookie(
            `${ASSIGNMENT_COOKIE_PREFIX}${slug}`,
            JSON.stringify({
                v: assignment.variation,
                // A string id: nextjs and the resolver both reject a numeric one.
                id: String(assignment.id),
                ...(subject ? { m: subject } : {}),
            }),
            ASSIGNMENT_MAX_AGE_SECONDS,
        )
    },
}
